// dashboardPrime.mjs — the dashboard's first requests, sent before the
// dashboard is on screen.
//
// The dashboard used to ask for its data only once it had mounted, which is
// after the sign-in check, after feature access and after its own code had
// arrived: every one of those a round trip to the API, one after the other.
// When it is already clear that the dashboard is where the person is going
// (a page load on its address with a stored token, or a sign-in that lands
// there), the requests are sent at that moment and the page picks the
// answers up when it mounts.
//
// Primed: what the page is drawn from (/dashboard) and the feeds behind
// "Needs your attention". Those belong together: a feed that answers after
// the page is up adds its row above "Your family" and pushes the page down
// under the reader. The slow, AI-backed /health-trends is deliberately not
// here; it fills in at the foot of the page whenever it is ready.
//
// One set is held at a time and it is handed over once. It is only handed to
// the sign-in it was sent for (same token), and only while it is fresh: a
// set nobody collected is not shown to somebody later.
//
// No imports and no browser APIs, so it runs under plain `node --test`
// (frontend/scripts/dashboard-first-load.test.js).

export const PRIMED_MAX_AGE_MS = 10 * 1000;

export const PRIMED_PATHS = [
  '/dashboard',
  '/billing/invoices',
  '/v01/doctor-consultation/mine',
  '/family-alerts',
];

// The addresses that show the dashboard (see the routes in App.js).
export function isDashboardPath(pathname) {
  return pathname === '/' || pathname === '/dashboard';
}

let primed = null;

export function primeDashboard(api, token, now = Date.now()) {
  if (!token) return;
  const requests = {};
  for (const path of PRIMED_PATHS) {
    const promise = api.get(path);
    // The page deals with a failure when it takes the request over. Until
    // then (or if it never does) a failure must not surface as an unhandled
    // rejection.
    promise.catch(() => {});
    requests[path] = promise;
  }
  primed = { token, at: now, requests };
}

// Hands the primed set to the page as a lookup: path -> the request already
// on its way, or null when there is none for it (the page then sends its
// own). Whatever the outcome nothing is left behind, so a second mount
// always sends its own requests.
export function takePrimedDashboard(token, now = Date.now()) {
  const entry = primed;
  primed = null;
  const usable = entry && token && entry.token === token && now - entry.at <= PRIMED_MAX_AGE_MS;
  return (path) => (usable && entry.requests[path]) || null;
}
